import * as React from "react";
import { graphql } from "react-apollo";
import gql from "graphql-tag";
import BOTable, {
  BOColumns,
  BOColumn,
  BOBody,
  BORow,
  BOCell,
} from "app/components/BOComponents/BOTable/BOTable";
import { formatDate, formatCurrency } from "app/utils/filters";
import { Link, withRouter } from "react-router-dom";
import UserDetailTopBar from "./Ops.Users.UserDetailTopBar";
import handleMutationError from "app/utils/handleMutationError";

type Props = {
  error: any;
  data: {
    user: { email: string; recurring_payment_schedules: any[] };
    refetch: any;
    loading: boolean;
  };
  match: any;
  query: any;
  history: any;

  cancelRecurringPayment: (variables: any) => any;
  runUnscheduledRecurringPayment: (variables: any) => any;
  setHeader: (data: { title: string; description: string }) => void;
};

class RecurringPaymentSchedules extends React.Component<Props> {
  componentDidMount() {
    this.runQuery();
  }

  cancelRecurringPayment(externalReference: string) {
    this.props
      .cancelRecurringPayment({ variables: { externalReference } })
      .then(() => {
        this.runQuery();
      })
      .catch((error) => {
        handleMutationError(
          `Failed to cancel recurring payment ${externalReference} due to: ${error}`
        );
      });
  }

  runUnscheduledRecurringPayment(externalReference: string) {
    this.props
      .runUnscheduledRecurringPayment({ variables: { externalReference } })
      .then((result) => {
        if (window.confirm(`Unscheduled payment started, view?`)) {
          const reference =
            result.data.run_unscheduled_recurring_payment.payments[0].reference;

          this.props.history.push(
            `/snapscan/ops/payments/${reference}/summary`
          );
        }
      })
      .catch((error) => {
        handleMutationError(
          `Failed to run unscheduled payment ${externalReference} due to: ${error}`
        );
      });
  }

  runQuery = (): void => {
    this.props.data
      .refetch()
      .then(({ data }): void => {
        const { email } = data.user || { email: "" };

        this.props.setHeader({
          title: `Ops > ${email}`,
          description: "Recurring payment schedules",
        });
      })
      .catch(handleMutationError);
  };

  render() {
    const {
      data: { loading },
    } = this.props;
    const { recurring_payment_schedules } = this.props.data.user || {
      recurring_payment_schedules: [],
    };

    const recurringPaymentSchedulesTable = {

      columns: (): JSX.Element => {
        return (
          <BOColumns>
            <BOColumn>Created At</BOColumn>
            <BOColumn>Next Payment Date</BOColumn>
            <BOColumn>Merchant</BOColumn>
            <BOColumn>Amount</BOColumn>
            <BOColumn>New Business Payment</BOColumn>
            <BOColumn />
            <BOColumn />
          </BOColumns>
        );
      },

      body: (): JSX.Element => {
        return (
          <BOBody>
            {recurring_payment_schedules.map((rps, i) => (
              <BORow key={i}>
                <BOCell>{formatDate(rps.created_at)}</BOCell>
                <BOCell>
                  {rps.next_payment_at}
                </BOCell>
                <BOCell>
                  <Link
                    className="blueLink"
                    to={`/snapscan/ops/merchants/${rps.new_business_payment.merchant.id}/payments`}
                  >
                    {rps.new_business_payment.merchant.name}
                  </Link>
                </BOCell>
                <BOCell>R{formatCurrency(rps.amount)}</BOCell>
                <BOCell>
                  <Link
                    className="blueLink"
                    to={`/snapscan/ops/payments/${rps.new_business_payment.reference}/summary`}
                  >
                    {rps.new_business_payment.status}
                  </Link>
                </BOCell>
                <BOCell>
                  <button
                    className="button statusButton"
                    style={{ zoom: 0.7 }}
                    onClick={() =>
                      this.cancelRecurringPayment(
                        rps.recurring_payment.external_reference
                      )
                    }
                  >
                    Cancel
                  </button>
                </BOCell>
                <BOCell>
                  <button
                    className="button statusButton"
                    style={{ zoom: 0.7 }}
                    onClick={() =>
                      this.runUnscheduledRecurringPayment(
                        rps.recurring_payment.external_reference
                      )
                    }
                  >
                    Run Unscheduled Payment
                  </button>
                </BOCell>
              </BORow>
            ))}
          </BOBody>
        );
      },
    };

    return (
      <React.Fragment>
        <UserDetailTopBar />
        <section className="padded">
          {loading ? (
            <div>loading...</div>
          ) : (
            <BOTable
              name="recurring_payment_schedules_table"
              columns={recurringPaymentSchedulesTable.columns()}
              body={recurringPaymentSchedulesTable.body()}
              loading={loading}
            />
          )}
        </section>
      </React.Fragment>
    );
  }
}

const userRecurringPaymentSchedulesQuery = gql`
  query UserRecurringPaymentSchedules(
    $external_reference: ID!
    $offset: Int
    $limit: Int
  ) {
    user(external_reference: $external_reference) {
      email
      recurring_payment_schedules(
        active: true
        offset: $offset
        limit: $limit
      ) {
        id
        recurring_payment {
          external_reference
          created_at
        }
        created_at
        amount
        next_payment_at
        new_business_payment {
          reference
          status
          merchant {
            id
            name
          }
        }
      }
    }
  }
`;

const cancelRecurringPayment = gql`
  mutation cancel_recurring_payment($externalReference: ID!) {
    cancel_recurring_payment(external_reference: $externalReference) {
      id
    }
  }
`;

const runUnscheduledRecurringPayment = gql`
  mutation runUnscheduledRecurringPayment($externalReference: ID!) {
    run_unscheduled_recurring_payment(external_reference: $externalReference) {
      payments {
        reference
      }
    }
  }
`;

const UserRecurringPaymentSchedulesWithData = graphql<any, any>(
  userRecurringPaymentSchedulesQuery,
  {
    options(props: any) {
      return {
        fetchPolicy: "network-only",
        variables: {
          external_reference: props.match.params.id,
          offset: 0,
          limit: 30,
        },
      };
    },
  }
)(
  graphql<any, any>(runUnscheduledRecurringPayment, {
    name: "runUnscheduledRecurringPayment",
  })(
    graphql<any, any>(cancelRecurringPayment, {
      name: "cancelRecurringPayment",
    })(withRouter(RecurringPaymentSchedules as any))
  )
);

export default UserRecurringPaymentSchedulesWithData;
